import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bytesToHuman } from '@wit-support/ui-domain';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { Button, ConfirmDialog, FileDropZone, InlineNotice, Meter, } from '@wit-support/ui';
import { CircleAlert, CircleCheck, RotateCcw, X } from 'lucide-react';
import { formatPercent } from '../format.js';
import { MEDIA_KEY, describeAdsError } from '../shared.js';
import { ADS_MEDIA_ACCEPT, isRetryableCompleteError, uploadAsset } from '../upload.js';
import { mediaFacts } from './media-sources.js';
let seq = 0;
/** Отказ загрузки словами. */
export function describeUploadError(err) {
    if (err instanceof ModuleRequestError &&
        (err.upstreamCode === 'upload-expired' || err.status === 410)) {
        return 'Срок загрузки истёк — загрузите файл заново';
    }
    return describeAdsError(err, 'write');
}
/** «Повторяем часть 3…» / «Повторяем части 1, 3…» — хранилище не ответило, идёт повтор. */
export function retryingText(parts) {
    return parts.length === 1
        ? `Повторяем часть ${parts[0]}…`
        : `Повторяем части ${parts.join(', ')}…`;
}
const active = (t) => t.status === 'checking' || t.status === 'uploading';
/**
 * Очередь загрузок с прогрессом: файлы грузятся параллельно, у каждого свои
 * части прямо в хранилище, ретраи и отмена. Живёт, пока смонтирован экран
 * (диалог выбора или страница медиатеки): при размонтировании незавершённые
 * загрузки отменяются — поэтому экран рендерит `UploadLeaveGuard` с `busy`.
 */
export function useUploads(onDone) {
    const qc = useQueryClient();
    const [tasks, setTasks] = useState([]);
    const aborts = useRef(new Map());
    // Части загружены, не прошло только завершение: «Повторить» повторяет его.
    const completeRetries = useRef(new Map());
    const patch = (id, p) => setTasks((cur) => cur.map((t) => (t.id === id ? { ...t, ...p } : t)));
    // Экран ушёл — его загрузки отменяются (и на сервере — `abort` в
    // `uploadInParts`): иначе части продолжают уходить в фоне. Загрузка, у
    // которой сорвалось только завершение (части в хранилище, сессия на сервере
    // жива), закрывается так же: повтор завершения с уже отменённым сигналом
    // сразу закрывает сессию через `abort`.
    useEffect(() => {
        const live = aborts.current;
        const retries = completeRetries.current;
        return () => {
            for (const [id, retry] of retries) {
                if (live.has(id))
                    continue; // идущий повтор завершения отменяется ниже сам
                const closed = new AbortController();
                closed.abort();
                void retry(closed.signal).catch(() => undefined);
            }
            retries.clear();
            for (const ac of live.values())
                ac.abort();
            live.clear();
        };
    }, []);
    const finished = useCallback((task, asset, deduplicated) => {
        completeRetries.current.delete(task.id);
        const done = {
            status: 'done',
            loaded: task.file.size,
            retryingParts: [],
            asset,
            deduplicated,
        };
        patch(task.id, done);
        void qc.invalidateQueries({ queryKey: MEDIA_KEY });
        onDone?.(asset, { ...task, ...done });
    }, [qc, onDone]);
    const run = useCallback((task) => {
        const ac = new AbortController();
        aborts.current.set(task.id, ac);
        completeRetries.current.delete(task.id);
        patch(task.id, { status: 'checking', loaded: 0, retryingParts: [], error: null });
        uploadAsset(task.file, {
            name: task.name,
            signal: ac.signal,
            onUploading: () => patch(task.id, { status: 'uploading' }),
            onFacts: (facts) => patch(task.id, { facts }),
            onProgress: (p) => patch(task.id, { loaded: p.loaded, retryingParts: p.retryingParts }),
            onCompleteFailed: (retry) => completeRetries.current.set(task.id, retry),
        })
            .then(({ asset, deduplicated }) => finished(task, asset, deduplicated))
            .catch((err) => {
            if (err instanceof Error && err.name === 'AbortError') {
                patch(task.id, { status: 'cancelled' });
            }
            else {
                patch(task.id, { status: 'error', error: describeUploadError(err) });
            }
        })
            .finally(() => aborts.current.delete(task.id));
    }, [finished]);
    /**
     * Повтор только завершения: байты уже в хранилище. «Отмена» работает и
     * здесь — обрывает ожидание и закрывает сессию на сервере.
     */
    const completeOnly = useCallback((task, retry) => {
        const ac = new AbortController();
        aborts.current.set(task.id, ac);
        patch(task.id, {
            status: 'uploading',
            loaded: task.file.size,
            retryingParts: [],
            error: null,
        });
        retry(ac.signal)
            .then((asset) => finished(task, asset, false))
            .catch((err) => {
            if (err instanceof Error && err.name === 'AbortError') {
                // сессия закрыта — «Повторить» грузит файл заново
                completeRetries.current.delete(task.id);
                patch(task.id, { status: 'cancelled' });
                return;
            }
            // окончательный отказ (истекла, не все части…) — следующий повтор грузит заново
            if (!isRetryableCompleteError(err))
                completeRetries.current.delete(task.id);
            patch(task.id, { status: 'error', error: describeUploadError(err) });
        })
            .finally(() => aborts.current.delete(task.id));
    }, [finished]);
    const start = useCallback((files) => {
        const created = files.map((file) => ({
            id: `up-${++seq}`,
            file,
            name: file.name.replace(/\.[^.]+$/, ''),
            loaded: 0,
            retryingParts: [],
            facts: null,
            status: 'checking',
            error: null,
            asset: null,
            deduplicated: false,
        }));
        setTasks((cur) => [...cur, ...created]);
        created.forEach(run);
    }, [run]);
    const cancel = (id) => aborts.current.get(id)?.abort();
    const retry = (id) => {
        const t = tasks.find((x) => x.id === id);
        if (!t)
            return;
        const again = completeRetries.current.get(id);
        if (t.status === 'error' && again)
            completeOnly(t, again);
        else
            run(t);
    };
    /** Убрать завершённые — идущие загрузки остаются. */
    const clearFinished = useCallback(() => setTasks((cur) => cur.filter(active)), []);
    // Сорвалось только завершение (ждёт «Повторить»): сессия на сервере жива,
    // уход со страницы её закроет — значит, тоже предупреждаем.
    const busy = tasks.some((t) => active(t) || (t.status === 'error' && completeRetries.current.has(t.id)));
    return { tasks, start, cancel, retry, clearFinished, busy };
}
// Смена только параметров адреса (фильтры медиатеки) экран не размонтирует —
// загрузки идут дальше, спрашивать не о чем.
const leavesScreen = ({ current, next }) => current.pathname !== next.pathname;
/**
 * Предупреждение при уходе, пока идёт загрузка: размонтирование экрана
 * отменяет незавершённые загрузки (`useUploads`). Переход внутри панели —
 * наш диалог «Остаться / Уйти»; закрытие или перезагрузка вкладки — штатный
 * вопрос браузера (`beforeunload`, текст задаёт браузер).
 */
export function UploadLeaveGuard({ busy }) {
    const blocker = useBlocker({
        shouldBlockFn: leavesScreen,
        enableBeforeUnload: true,
        disabled: !busy,
        withResolver: true,
    });
    return (_jsx(ConfirmDialog, { open: blocker.status === 'blocked', onOpenChange: (open) => {
            if (!open)
                blocker.reset?.();
        }, title: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0435\u0449\u0451 \u0438\u0434\u0451\u0442", description: "\u0415\u0441\u043B\u0438 \u0443\u0439\u0442\u0438 \u0441\u043E \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u044B, \u043D\u0435\u0437\u0430\u0432\u0435\u0440\u0448\u0451\u043D\u043D\u044B\u0435 \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0438 \u043E\u0442\u043C\u0435\u043D\u044F\u0442\u0441\u044F \u2014 \u0444\u0430\u0439\u043B\u044B \u043F\u0440\u0438\u0434\u0451\u0442\u0441\u044F \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0437\u0430\u043D\u043E\u0432\u043E.", cancelLabel: "\u041E\u0441\u0442\u0430\u0442\u044C\u0441\u044F", actionLabel: "\u0423\u0439\u0442\u0438", actionStyle: "danger", onConfirm: () => blocker.proceed?.() }));
}
/** Строки загрузок: имя, размер, полоса прогресса, отмена / повтор. */
export function UploadProgressList({ tasks, onCancel, onRetry, }) {
    if (tasks.length === 0)
        return null;
    return (_jsx("ul", { className: "flex flex-col gap-2", "data-testid": "upload-list", children: tasks.map((t) => (_jsxs("li", { className: "flex flex-col gap-1 text-sm", "data-status": t.status, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "min-w-0 flex-1 truncate font-medium", children: t.name }), _jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "upload-facts", children: [t.facts ? `${mediaFacts({ kind: 'video', ...t.facts })} · ` : '', bytesToHuman(t.file.size)] }), active(t) ? (_jsxs(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => onCancel(t.id), "aria-label": `Отменить загрузку ${t.name}`, className: "gap-1.5", children: [_jsx(X, { "aria-hidden": true, className: "h-3.5 w-3.5" }), "\u041E\u0442\u043C\u0435\u043D\u0430"] })) : null, t.status === 'error' || t.status === 'cancelled' ? (_jsxs(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => onRetry(t.id), className: "gap-1.5", children: [_jsx(RotateCcw, { "aria-hidden": true, className: "h-3.5 w-3.5" }), "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C"] })) : null, t.status === 'done' ? (_jsx(CircleCheck, { "aria-label": "\u0417\u0430\u0433\u0440\u0443\u0436\u0435\u043D\u043E", className: "h-4 w-4 text-[var(--color-state-success-fg)]" })) : null] }), t.status === 'checking' ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u041F\u0440\u043E\u0432\u0435\u0440\u044F\u0435\u043C \u0444\u0430\u0439\u043B\u2026" })) : null, t.status === 'uploading' ? (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Meter, { value: t.file.size > 0 ? (t.loaded / t.file.size) * 100 : 0, label: `Загрузка ${t.name}`, className: "flex-1" }), _jsx("span", { className: "w-12 text-right text-xs tabular-nums", children: formatPercent(t.loaded, t.file.size) })] })) : null, t.status === 'uploading' && t.retryingParts.length > 0 ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", role: "status", children: retryingText(t.retryingParts) })) : null, t.status === 'done' ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: t.deduplicated ? 'Такой файл уже есть в медиатеке — взят он.' : 'Загружено.' })) : null, t.status === 'cancelled' ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043E\u0442\u043C\u0435\u043D\u0435\u043D\u0430" })) : null, t.error ? (_jsx(InlineNotice, { variant: "danger", icon: CircleAlert, as: "span", children: t.error })) : null] }, t.id))) }));
}
/** Подсказка о типах и размерах файлов (пределы — из `/health` auto-ads). */
export function maxBytesHint(health) {
    const m = health?.mediaMaxBytes;
    return m
        ? `JPEG и PNG до ${bytesToHuman(m.image)}, MP4 и MOV до ${bytesToHuman(m.video)}.`
        : 'JPEG, PNG, MP4 или MOV.';
}
/**
 * Перетащить или выбрать файлы → загрузка с прогрессом. Кабинеты не
 * выбираются: файл переносится в кабинет запуска сам.
 */
export function UploadDropArea({ uploads, health, }) {
    const [rejected, setRejected] = useState([]);
    const maxBytes = health?.mediaMaxBytes
        ? Math.max(health.mediaMaxBytes.image, health.mediaMaxBytes.video)
        : undefined;
    return (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(FileDropZone, { accept: ADS_MEDIA_ACCEPT, multiple: true, ...(maxBytes !== undefined ? { maxBytes } : {}), title: "\u041F\u0435\u0440\u0435\u0442\u0430\u0449\u0438\u0442\u0435 \u043A\u0430\u0440\u0442\u0438\u043D\u043A\u0438 \u0438 \u0432\u0438\u0434\u0435\u043E \u0441\u044E\u0434\u0430", hint: maxBytesHint(health), onFiles: (files) => {
                    setRejected([]);
                    uploads.start(files);
                }, onReject: setRejected }), rejected.length > 0 ? (_jsx(InlineNotice, { variant: "danger", as: "div", children: rejected.map((r) => r.message).join('; ') })) : null, _jsx(UploadProgressList, { tasks: uploads.tasks, onCancel: uploads.cancel, onRetry: uploads.retry })] }));
}
//# sourceMappingURL=uploads.js.map